/**
 * HTTP client for the OCR Evals module.
 *
 * Points at ruby-hq's `/api/ocr-evals/*` proxy (forwarded to document-manager
 * with a signed admin-session header). The eval tool is admin-global — there is
 * NO clinic scoping, so callers never append `?client=`. `apiFetch` only adds
 * the `X-Client-Id` header when a clinic happens to be selected; doc-manager's
 * eval routes ignore it.
 *
 * Plan: `docs/plans/2026-06-09-ocr-eval-harness.md`.
 */

import { apiFetch, getCsrfToken } from '@/lib/api';
import type {
  CreateRunBody,
  CreateRunResponse,
  DefaultPromptResponse,
  EvalDocDetail,
  ListDocumentsResponse,
  ListGoldensResponse,
  ListResultsResponse,
  ListRunsResponse,
  OcrEvalConfigResponse,
  RawExtraction,
  SaveGoldenResponse,
  UploadResponse,
} from './types';

const BASE = '/api/ocr-evals';

/** Feature-flag echo — `{enabled}` is true only for admins on staging. Mounted
 *  at `/api/ocr-eval-config` (NOT under the proxied `/api/ocr-evals/*`). */
export const ocrEvalConfigApi = {
  get: (): Promise<OcrEvalConfigResponse> => apiFetch<OcrEvalConfigResponse>('/api/ocr-eval-config'),
};

export const ocrEvalApi = {
  // ---- prompt ----
  defaultPrompt: (): Promise<DefaultPromptResponse> =>
    apiFetch<DefaultPromptResponse>(`${BASE}/default-prompt`),

  // ---- documents ----
  listDocuments: (archived: boolean): Promise<ListDocumentsResponse> =>
    apiFetch<ListDocumentsResponse>(`${BASE}/documents?archived=${archived ? 'true' : 'false'}`),

  getDocument: (id: string): Promise<EvalDocDetail> =>
    apiFetch<EvalDocDetail>(`${BASE}/documents/${encodeURIComponent(id)}`),

  renameDocument: (id: string, displayName: string): Promise<{ ok: boolean }> =>
    apiFetch<{ ok: boolean }>(`${BASE}/documents/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    }),

  archiveDocument: (id: string, archived: boolean): Promise<{ ok: boolean }> =>
    apiFetch<{ ok: boolean }>(`${BASE}/documents/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    }),

  /** Hard purge — 409 if the doc isn't archived first (surface the message). */
  deleteDocument: (id: string): Promise<{ ok: boolean }> =>
    apiFetch<{ ok: boolean }>(`${BASE}/documents/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /** `<img src>` URL for the source image (PDF → chosen page rendered as PNG). */
  imageUrl: (id: string): string => `${BASE}/documents/${encodeURIComponent(id)}/image`,

  saveGolden: (id: string, golden: RawExtraction): Promise<SaveGoldenResponse> =>
    apiFetch<SaveGoldenResponse>(`${BASE}/documents/${encodeURIComponent(id)}/golden`, {
      method: 'PUT',
      body: JSON.stringify({ golden }),
    }),

  /**
   * Multipart upload. Field name is `file`. For a PDF the caller passes the
   * chosen requisition page via `?page=N`. Uses raw `fetch` (not apiFetch)
   * because the body is FormData — apiFetch forces `Content-Type: application/
   * json`. Mirrors apiFetch's CSRF + credentials.
   */
  uploadDocument: async (file: File, page?: number): Promise<UploadResponse> => {
    const form = new FormData();
    form.append('file', file);
    const url = page != null ? `${BASE}/documents?page=${page}` : `${BASE}/documents`;
    const headers: Record<string, string> = {};
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || `Upload failed: ${res.status}`);
    }
    return res.json() as Promise<UploadResponse>;
  },

  // ---- runs ----
  listRuns: (): Promise<ListRunsResponse> => apiFetch<ListRunsResponse>(`${BASE}/runs`),

  createRun: (body: CreateRunBody): Promise<CreateRunResponse> =>
    apiFetch<CreateRunResponse>(`${BASE}/runs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteRun: (id: number): Promise<{ ok: boolean }> =>
    apiFetch<{ ok: boolean }>(`${BASE}/runs/${id}`, { method: 'DELETE' }),

  // ---- results + goldens ----
  results: (runIds: number[], docIds: string[]): Promise<ListResultsResponse> => {
    const q = new URLSearchParams();
    if (runIds.length) q.set('runIds', runIds.join(','));
    if (docIds.length) q.set('docIds', docIds.join(','));
    const qs = q.toString();
    return apiFetch<ListResultsResponse>(`${BASE}/results${qs ? `?${qs}` : ''}`);
  },

  goldens: (): Promise<ListGoldensResponse> => apiFetch<ListGoldensResponse>(`${BASE}/goldens`),
};
